/**
 * RolePageRoute.jsx — /for/:slug gate. Unpublished pages stay hidden.
 * Shaped by prompt 38; MKT-01, MKT-08, MKT-11, MKT-20, MKT-23, MKT-25.
 * D-38-01, D-38-03.
 * Invariants: a role page never grants access. /for/patient is not handled
 * here (it redirects before this route). Unknown and unauthorized slugs use
 * the same unmatched fallback as /for/not-a-role. Auth, rules, and role
 * storage are unchanged.
 * Related: fusionMsiRoleSplashRoutes.jsx, rolePageAccess.js, RolePage.jsx.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useSearchParams } from 'react-router-dom';
import Loading from 'components/Loading';
import { useAuth } from 'hooks/useAuth';
import { getRoutableRolePage } from '../marketing/rolePageCopyData';
import { rolePageView } from '../marketing/rolePageAccess';
import { RolePageBody, RoleStaffSignIn } from './RolePage';

function RolePageGate({ page, unmatchedElement }) {
  const [searchParams] = useSearchParams();
  const { user, isGlobalAdmin, userLoading } = useAuth();

  if (userLoading) return <Loading page />;

  const view = rolePageView({
    page,
    signedIn: Boolean(user),
    isGlobalAdmin: Boolean(isGlobalAdmin),
    previewQuery: searchParams.get('preview') === '1',
  });

  if (view === 'not-found') return unmatchedElement;
  if (view === 'staff-sign-in') return <RoleStaffSignIn page={page} />;
  return <RolePageBody page={page} view={view} />;
}

export default function RolePageRoute({ unmatchedElement, slug }) {
  const { roleSlug: paramSlug } = useParams();
  const roleSlug = slug || paramSlug;
  const page = getRoutableRolePage(roleSlug);
  // Unknown slugs skip auth so /for/not-a-role stays the existing fallback.
  if (!page) return unmatchedElement;
  return <RolePageGate page={page} unmatchedElement={unmatchedElement} />;
}

RolePageGate.propTypes = {
  page: PropTypes.object.isRequired,
  unmatchedElement: PropTypes.node,
};

RolePageRoute.propTypes = {
  unmatchedElement: PropTypes.node,
  slug: PropTypes.string,
};
